import { Modal, Box, Typography, TextField, Button, FormControl, FormGroup, FormLabel, Switch, MenuItem, Select } from "@mui/material";
import ShireImageUpload from "components/ShireImageUpload";
import { useEffect, useState } from "react";
import { UpdateVoucherDto, VoucherStatus } from "./VoucherType";
import ShireDatePicker from "components/ShireDatePicker";
import dayjs from "dayjs";
import { publishMessage } from "components/ShireMessage";
import { deleteImage, uploadImage } from "services/FileService";
import { createVoucher, updateVoucher } from "services/VoucherService";
import ShireImageCard from "components/ShireImageCard";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { VOUCHER_EVENT_TYPE } from "utils/constant";

export default function VoucherModal({ open, handleClose, isEdit, initialValues, reloadTableData }: { open: boolean, handleClose: () => void, isEdit: boolean, initialValues: UpdateVoucherDto | null, reloadTableData: () => void }) {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [voucher, setVoucher] = useState<UpdateVoucherDto>(initialValues || {} as UpdateVoucherDto);

  useEffect(() => {
    if (initialValues && isEdit) {
      setVoucher(initialValues);
      setImageFile(
        initialValues.imageUrl ? new File([], initialValues.imageUrl) : null
      );
    } else if (!isEdit && initialValues) {
      setVoucher(initialValues);
      setImageFile(null);
    }
  }, [initialValues, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVoucher(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const voucherData = { ...voucher };
    if (!voucher.imageUrl && !imageFile) {
      publishMessage({
        message: "Vui lòng thêm ảnh voucher",
        variant: "error",
      });
      return;
    }

    if (!voucher.imageUrl && imageFile) {
      const formData = new FormData();
      formData.append("image", imageFile);

      const { url, public_id: publicId } = await uploadImage(formData, 'vouchers');
      voucherData.imageUrl = url;
      voucherData.externalId = publicId;
      setVoucher(prev => ({ ...prev, imageUrl: url, externalId: publicId }));
    }

    if (isEdit) {
      await updateVoucher(voucherData);
    } else {
      await createVoucher(voucherData);
    }
    reloadTableData();
    handleClose();
  };

  const handleDeleteImage = async () => {
    if (voucher.externalId) {
      await deleteImage(voucher.externalId);
    }
    setVoucher(prev => ({ ...prev, imageUrl: "", externalId: "" }));
    setImageFile(null);
  };

  return <Modal open={open} onClose={handleClose}>
    <Box
      component="form"
      sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '80vw', bgcolor: 'background.paper', boxShadow: 24, p: 4, overflow: 'auto', maxHeight: '90vh' }}
      onSubmit={handleSubmit}
    >
      <Typography variant="h6">{isEdit ? 'Edit Voucher' : 'Add Voucher'}</Typography>
      {/* Name and Description row */}
      <FormGroup sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="name">Tên voucher</FormLabel>
          <TextField
            id="name"
            name="name"
            placeholder="Tên voucher"
            value={voucher.name}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="code">Mã voucher</FormLabel>
          <TextField
            id="code"
            name="code"
            placeholder="Mã voucher (Ví dụ: DISCOUNT10)"
            value={voucher.code}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl sx={{ gridColumn: '1 / -1' }}>
          <FormLabel htmlFor="description">Mô tả</FormLabel>
          <Box sx={{ 
            border: '1px solid #ccc', 
            borderRadius: 1,
            '& .ql-editor': {
              minHeight: '120px',
              fontSize: '14px',
              fontFamily: 'inherit'
            },
            '& .ql-toolbar': {
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              borderBottom: '1px solid #ccc'
            }
          }}>
            <ReactQuill
              theme="snow"
              value={voucher.description}
              onChange={(content) => setVoucher({ ...voucher, description: content })}
              placeholder="Mô tả voucher (hỗ trợ định dạng văn bản)"
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  [{ 'align': [] }],
                  ['link'],
                  ['clean']
                ]
              }}
            />
          </Box>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="voucherType">Loại voucher</FormLabel>
          <TextField
            id="voucherType"
            name="voucherType"
            placeholder="Loại voucher"
            value={voucher.voucherType}
            onChange={handleChange}
            disabled
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="exchangePoints">Điểm quy đổi</FormLabel>
          <TextField
            id="exchangePoints"
            name="exchangePoints"
            placeholder="Điểm quy đổi (điểm >= 0)"
            type="number"
            value={voucher.exchangePoints}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="discountType">Giảm giá theo</FormLabel>
          <TextField
            id="discountType"
            name="discountType"
            placeholder="Giảm giá theo"
            value={voucher.discountType}
            onChange={handleChange}
            disabled
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="discountValue">Giá trị giảm</FormLabel>
          <TextField
            id="discountValue"
            name="discountValue"
            placeholder="Giá trị giảm (đồng)"
            type="number"
            value={voucher.discountValue}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="maxCount">Số lượng tối đa (0: không giới hạn)</FormLabel>
          <TextField
            id="maxCount"
            name="maxCount"
            placeholder="Số lượng tối đa (0: không giới hạn)"
            type="number"
            value={voucher.maxCount}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="expirePeriod">Thời gian sử dụng (ngày) (0: không giới hạn)</FormLabel>
          <TextField
            id="expirePeriod"
            name="expirePeriod"
            placeholder="Thời gian sử dụng (ngày) (0: không giới hạn)"
            type="number"
            value={voucher.expirePeriod}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="startDate">Ngày bắt đầu</FormLabel>
          <ShireDatePicker
            value={dayjs(voucher.startDate)}
            onChange={(date) => setVoucher(prev => ({ ...prev, startDate: date?.toDate() ?? new Date() }))}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="endDate">Ngày kết thúc</FormLabel>
          <ShireDatePicker
            value={dayjs(voucher.endDate)}
            onChange={(date) => setVoucher(prev => ({ ...prev, endDate: date?.toDate() ?? new Date() }))}
          />
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="status">Trạng thái</FormLabel>
          <Select
            id="status"
            name="status"
            value={voucher.status}
            onChange={(e: any) => setVoucher(prev => ({ ...prev, status: e.target.value as VoucherStatus }))}
            disabled={!isEdit}
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel htmlFor="isPublic">Công khai (có thể sử dụng bởi tất cả người dùng)</FormLabel>
          <Switch
            id="isPublic"
            name="isPublic"
            checked={voucher.isPublic}
            onChange={(e) => setVoucher(prev => ({ ...prev, isPublic: e.target.checked }))}
          />
        </FormControl>
        {!voucher.isPublic && <FormControl sx={{ flex: 1 }}>
        <FormLabel htmlFor="status">Sự kiện</FormLabel>
          <Select
            id="eventType"
            name="eventType"
            value={voucher.eventType}
            onChange={(e: any) => setVoucher(prev => ({ ...prev, eventType: e.target.value as typeof VOUCHER_EVENT_TYPE }))}
          >
            {Object.values(VOUCHER_EVENT_TYPE).map((eventType) => (
              <MenuItem value={eventType}>{eventType}</MenuItem>
            ))}
          </Select>
        </FormControl>}
      </FormGroup>

      <FormControl sx={{ mt: 2, width: '100%' }}>
        <FormLabel htmlFor="photos">Ảnh voucher</FormLabel>
        {voucher.imageUrl ?
          <ShireImageCard
            imageUrl={voucher.imageUrl}
            id={voucher.id}
            title={voucher.name}
            description={voucher.description}
            isActive={voucher.status === "ACTIVE"}
            onToggleActive={undefined}
            onDelete={handleDeleteImage} /> :
          <ShireImageUpload
            file={imageFile}
            setFile={setImageFile}
            minWidth={50}
            minHeight={50}
            maxWidth={200}
            maxHeight={200}
            label="Upload Voucher Image"
            accept="image/jpeg,image/png,image/webp,image/jpg"
          />
        }
      </FormControl>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button onClick={handleClose} sx={{ mr: 1 }}>Cancel</Button>
        <Button type="submit" variant="contained" color="primary">{isEdit ? 'Update' : 'Add'}</Button>
      </Box>
    </Box>
  </Modal>;
}